import { Box } from "@caspeco/casper-ui-library";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { Modal, ModalContent, ModalOverlay, Slide, useBreakpointValue } from "@chakra-ui/react";
import { useSlideModal } from "components/use-slide-modal";
import {
    lockDocumentScroll,
    releaseDocumentScroll,
} from "components/slide-modal/document-scroll-lock";
import { AppMode, getAppMode } from "types/app-mode";
import { ReactNode, useEffect } from "react";
import ConditionalWrapper from "components/conditional-wrapper";

/**
 * Slide animation direction options
 */
type SlideDirection = "right" | "bottom" | "left" | "top";

/**
 * Modal display variant
 * - centered: Modal is centered on desktop, fullscreen on mobile
 * - fullscreen: Modal is always fullscreen
 */
type SlideModalVariant = "centered" | "fullscreen";

interface SlideModalProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback when modal should close */
    onClose: () => void;
    /** Direction to slide in/out from */
    slideDirection?: SlideDirection;
    /** Display variant */
    variant?: SlideModalVariant;
    /** Chakra Modal size (only applies to centered variant on desktop) */
    size?: string;
    /** Whether clicking overlay closes the modal */
    closeOnOverlayClick?: boolean;
    /** Custom z-index for the slide container */
    zIndex?: number;
    /** Children to render inside ModalContent */
    children: ReactNode;
    /** Additional props to pass to ModalContent */
    modalContentProps?: Record<string, unknown>;
}

const appMode = getAppMode();

/**
 * A modal component with slide animations that can change direction on close.
 *
 * This component wraps Chakra UI's Modal and Slide components to provide
 * smooth slide-in/slide-out animations with the ability to change the slide
 * direction before closing (e.g., slide from right when opening, but slide
 * down when confirming).
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const [direction, setDirection] = useState<SlideDirection>("right");
 *
 *   const handleConfirm = () => {
 *     setDirection("bottom"); // Change direction before closing
 *     // Wait for any async operations...
 *     setIsOpen(false); // This will slide down
 *   };
 *
 *   return (
 *     <SlideModal
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *       slideDirection={direction}
 *       variant="centered"
 *       size="md"
 *     >
 *       <ModalBody>Content here</ModalBody>
 *       <ModalFooter>
 *         <Button onClick={handleConfirm}>Confirm</Button>
 *       </ModalFooter>
 *     </SlideModal>
 *   );
 * }
 * ```
 */
function SlideModal({
    isOpen,
    onClose,
    slideDirection: externalDirection = "right",
    variant = "centered",
    size = "md",
    closeOnOverlayClick = false,
    zIndex = 1400,
    children,
    modalContentProps = {},
}: SlideModalProps) {
    const isModal = appMode === AppMode.Modal;
    const { slideDirection, setSlideDirection, isModalOpen, slideIn, ANIMATION_DURATION } =
        useSlideModal(isOpen, externalDirection);

    // Sync external direction changes with internal hook
    // This allows parent to change direction while modal is visible
    useEffect(() => {
        if (externalDirection !== slideDirection) {
            setSlideDirection(externalDirection);
        }
    }, [externalDirection, slideDirection, setSlideDirection]);

    // In the SDK modal this dialog fills the whole iframe, so the document
    // behind it keeps a scrollbar that moves nothing the guest can see.
    // Chakra's own blockScrollOnMount targets body, but the scroller here is
    // the document element, so it never takes effect.
    useEffect(() => {
        if (!isModal || !isModalOpen) return;

        lockDocumentScroll();
        return releaseDocumentScroll;
    }, [isModal, isModalOpen]);

    const isFullscreen = variant === "fullscreen";

    const shouldShowOverlay = useBreakpointValue({
        base: false,
        md: true,
        lg: true,
        xl: true,
    });

    return (
        <Modal
            isOpen={isModalOpen}
            onClose={onClose}
            closeOnOverlayClick={closeOnOverlayClick}
            size={size}
            motionPreset="none"
        >
            {shouldShowOverlay && <ModalOverlay />}
            <ConditionalWrapper
                condition={isModal}
                wrapper={(children) => (
                    <Slide
                        direction={slideDirection}
                        in={slideIn}
                        style={{
                            zIndex,
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                        transition={{
                            enter: { duration: ANIMATION_DURATION / 1000, ease: "easeOut" },
                            exit: { duration: ANIMATION_DURATION / 1000, ease: "easeIn" },
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems={
                                isFullscreen ? "stretch" : { base: "stretch", md: "center" }
                            }
                            justifyContent={
                                isFullscreen ? "stretch" : { base: "stretch", md: "center" }
                            }
                            width="100%"
                            height="100%"
                        >
                            {children}
                        </Box>
                    </Slide>
                )}
            >
                <ModalContent
                    display="flex"
                    flexDirection="column"
                    m="0px"
                    borderRadius={isFullscreen ? 0 : undefined}
                    width={isFullscreen ? "100%" : undefined}
                    maxW={isFullscreen ? "100%" : undefined}
                    height={isFullscreen ? "100%" : undefined}
                    maxH="100dvh"
                    {...modalContentProps}
                >
                    {children}
                </ModalContent>
            </ConditionalWrapper>
        </Modal>
    );
}

export { SlideModal };
export type { SlideDirection };
